/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: utils/formDefaults.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    Default form data factories for Extensions module.
────────────────────────────────────────────────────────────────*/

import { DEFAULT_LANGUAGE, DEFAULT_LANGUAGE_CODE, DEFAULT_VOICE } from '../../../data/formOptions';
import type {
  ExtensionFormState,
  VoicemailSettingsState,
  RecordingSettingsState,
  ContactInfoState,
  FollowMeSettingsState,
  PersonalAssistantState,
} from '../types';
import { generateDefaultPassword, generateDefaultSecret, generateVoicemailPin } from './passwordGenerators';

const DEFAULT_VM_LANGUAGE = DEFAULT_LANGUAGE_CODE.split('-')[0] || 'en';

/**
 * Creates default extension form data
 */
export function createDefaultFormData(): ExtensionFormState {
  return {
    extension: '',
    name: '',
    extension_type: 'sip',
    languageCode: DEFAULT_LANGUAGE_CODE,
    voice: DEFAULT_VOICE,
    soundPrefix: '',
    enabled: true,
    classOfServicesId: null,
    dialProfileId: null,
    callCenterAgentProfileId: null,
    featuresPassword: generateDefaultPassword(),
    emailAddress: '',
    billingEnabled: false,
    voicemailEnabled: false,
    blfSupervisionEnabled: false,
    internalCidName: '',
    internalCidNumber: '',
    externalCidName: '',
    externalCidNumber: '',
    emergencyCidName: '',
    emergencyCidNumber: '',
    accountCode: '',
    tollAllow: 'domestic,international,local',
    callTimeout: 30,
    maxCalls: 0,
    callWaiting: true,
    rejectAnonymous: false,
    autoAnswer: false,
    holdMusic: '',
    privacy: 'none',
    callScreen: false,
    presenceId: '',
    ringtone: '',
    effectiveCallerIdName: '',
    effectiveCallerIdNumber: '',
    pinless: false,
    forwardTo: '',
    deviceUsername: '',
    devicePassword: generateDefaultSecret(),
    deviceDescription: '',
    deviceSipProfileId: null,
    deviceDispatchLocationId: null,
    deviceMaxContacts: '5',
    deviceOutboundCodecs: 'PCMU,PCMA',
    deviceInboundCodecs: 'PCMU,PCMA',
    deviceDtmfMode: 'rfc4733',
    // Portal Access
    portalEnabled: false,
    portalUser: '',
    portalPassword: '',
    softphoneEnabled: false,
    // SMS Integration
    smsNumberId: null,
    chatbotEnabled: false,
    chatbotAgentId: null,
    // Simultaneous Translation
    simultaneousTranslationEnabled: false,
    aiTranslatorProfileId: null,
    // Fax (inline)
    faxEnabled: false,
    faxNumber: '',
    faxEmailTo: '',
    faxEmailFrom: '',
    faxDeviceId: null,
  };
}

/**
 * Creates default voicemail settings data
 */
export function createDefaultVoicemailData(): VoicemailSettingsState {
  return {
    password: generateVoicemailPin(),
    email: '',
    maxMessages: 100,
    notificationEnabled: true,
    transcribeEnabled: false,
    emailAttachment: true,
    language: DEFAULT_VM_LANGUAGE,
    timezone: 'America/Managua',
    greeting: '',
    storagePath: '',
    emailTemplate: '',
  };
}

/**
 * Creates default recording settings data
 */
export function createDefaultRecordingSettings(): RecordingSettingsState {
  return {
    recordIncoming: false,
    recordOutgoing: false,
    recordInternal: false,
    recordOnDemand: false,
    recordingFormat: 'wav',
    recordPath: '/recordings',
    recordTemplate: '${caller_id_number}-${uuid}.wav',
  };
}

/**
 * Creates default contact info data
 */
export function createDefaultContactInfo(): ContactInfoState {
  return {
    name: '',
    email: '',
    phone: '',
    notes: '',
  };
}

/**
 * Creates default Follow Me settings data
 */
export function createDefaultFollowMeData(): FollowMeSettingsState {
  return {
    callForwardEnabled: false,
    callForwardDestination: '',
    callForwardTimeGroupId: null,
    onBusyEnabled: false,
    onBusyDestination: '',
    onBusyTimeGroupId: null,
    noAnswerEnabled: false,
    noAnswerDestination: '',
    noAnswerTimeGroupId: null,
    notRegisteredEnabled: false,
    notRegisteredDestination: '',
    notRegisteredTimeGroupId: null,
    enabled: false,
    destinations: [],
    ringStrategy: 'simultaneous',
    skipBusy: false,
    ignoreBusy: false,
    doNotDisturb: false,
    doNotDisturbTimeGroupId: null,
  };
}

/**
 * Creates default Personal Assistant data
 */
export function createDefaultPersonalAssistantData(): PersonalAssistantState {
  return {
    enabled: false,
    instructionMessageRecordingId: null,
    options: [],
    timeout: 10,
    maxRetries: 3,
    fallbackAction: '', // Will be set via ModuleDestinationSelect
  };
}

